import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, Circle, InfoIcon, LoaderCircle, RefreshCw } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { useEffect, useState, useMemo } from "react";
import { useEmailHealthTestStatus } from "@/hooks/use-postmaster";
import { useQueryClient } from "@tanstack/react-query";
import { SUBSCRIPTION_QUERY_KEY } from "@/hooks/use-subscription";

interface EmailTestStatus {
  type: "test_created" | "mail_sent" | "mail_received" | "completed";
  status: "initialized" | "pending" | "completed";
  step: string;
  message: string;
}

// Simple notification box for completed tests
export const TestCompletedNotification = ({
  domain,
  onRefresh,
}: {
  domain: string;
  onRefresh: () => void;
}) => {
  const queryClient = useQueryClient();
  const handleCheckClick = () => {
    queryClient.invalidateQueries({
      queryKey: ["stored-dns-records", domain],
    });
    onRefresh();
  };

  return (
    <Card
      className={cn(
        "col-span-3 w-full mt-5 bg-gradient-to-br from-blue-50 to-blue-100/50 overflow-hidden border-blue-200/50 shadow-sm"
      )}
    >
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between p-6">
          <div className="mb-4 md:mb-0">
            <h3 className="text-lg font-medium text-blue-800">
              New Test is Completed Successfully!
            </h3>
            <p className="mt-1 text-sm text-blue-700/80">
              Please click the refresh button to view the latest results.
            </p>
          </div>
          <Button
            onClick={handleCheckClick}
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
        <div className="bg-blue-100 border-t border-blue-200 px-6 py-3 flex items-center text-sm text-blue-700">
          <InfoIcon className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>Please refresh the page to view the latest results.</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Component for the Run Another Test box
export const RunAnotherTestBox = () => {
  return (
    <div className="w-full bg-white border rounded-lg shadow-sm overflow-hidden">
      <div className="p-4">
        <h2 className="text-blue-700 text-xl font-medium">Run Another Test</h2>
        <p className="text-gray-600 text-sm">
          Check your score with the latest data
        </p>
      </div>

      <div className="flex items-center justify-between bg-blue-50 border-t border-blue-100 p-4">
        <div className="flex items-center text-blue-700 text-sm">
          <InfoIcon className="h-5 w-5 mr-2 text-blue-500" />
          <p>
            After completing a new test, please refresh the page to view the
            most up-to-date results.
          </p>
        </div>

        <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-full ml-4 px-4 py-2 flex items-center">
          <RefreshCw className="mr-2 h-4 w-4" /> Start New Test
        </Button>
      </div>
    </div>
  );
};

interface EmailTestProgressLoaderProps {
  domain: string;
  isRunningTest: boolean;
  onComplete: () => void;
}

interface Step {
  id: string;
  label: string;
  completed: boolean;
}

export const EmailTestProgressLoader = ({
  domain,
  isRunningTest,
  onComplete,
}: EmailTestProgressLoaderProps) => {
  const [steps, setSteps] = useState<Step[]>([
    {
      id: "test_created",
      label: "Initializing Email Test",
      completed: false,
    },
    {
      id: "mail_sent",
      label: "Sending Test Email",
      completed: false,
    },
    {
      id: "mail_received",
      label: "Checking Email Receipt",
      completed: false,
    },
    {
      id: "completed",
      label: "Completing Test",
      completed: false,
    },
  ]);

  const { data } = useEmailHealthTestStatus(domain, isRunningTest);

  const emailTestStatus = useMemo(
    () => (Array.isArray(data) ? data : []) as EmailTestStatus[],
    [data]
  );

  // Get the latest status message
  const latestStatus = emailTestStatus?.[emailTestStatus.length - 1];
  const isComplete = latestStatus?.type === "completed";

  useEffect(() => {
    if (emailTestStatus.length === 0) return;

    setSteps((pre) =>
      pre.map((step) => {
        const stepExists = emailTestStatus.some(
          (status) => status.type === step.id
        );
        return {
          ...step,
          completed: stepExists,
        };
      })
    );
  }, [emailTestStatus]);

  useEffect(() => {
    if (isComplete) {
      onComplete();
    }
  }, [isComplete, onComplete]);

  console.log("latestStatus", latestStatus);

  if (!isRunningTest || !emailTestStatus) return null;

  return (
    <Card
      className={cn(
        "col-span-3 w-full mt-5 bg-gradient-to-br from-blue-50 to-blue-100/50 overflow-hidden border-blue-200/50 shadow-sm"
      )}
    >
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between p-6">
          <div className="max-w-4xl mx-auto flex items-center w-full mb-3">
            {steps.map((step) => {
              const stepExists = emailTestStatus.some(
                (status) => status.type === step.id
              );
              return (
                <StepperItem
                  key={step.id}
                  label={step.label}
                  completed={stepExists}
                  loading={step.id === latestStatus?.type}
                />
              );
            })}
          </div>
        </div>
        <div className="bg-blue-100 border-t border-blue-200 px-6 py-3 flex items-center text-sm text-blue-700">
          <InfoIcon className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>
            {latestStatus?.message ||
              "After completing a new test, please refresh the page to view the most up-to-date results."}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const StepperItem = ({
  label,
  completed,
  loading,
}: {
  label: string;
  completed: boolean;
  loading: boolean;
}) => {
  return (
    <div className="flex items-center flex-1 last:flex-none">
      {/* indicator */}
      <div className="flex flex-col gap-1 items-center relative">
        <div
          className={cn(
            "flex items-center justify-center rounded-full size-7 bg-neutral-200  text-white",
            loading && "bg-blue-500",
            completed && "bg-blue-500"
          )}
        >
          {completed ? (
            <Check className="size-4" />
          ) : loading ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Circle className="size-4" />
          )}
        </div>
        <div className="text-xs text-primary absolute top-full whitespace-nowrap">
          {label}
        </div>
      </div>
      <div
        className={cn(
          "m-0.5 bg-neutral-200 h-0.5 w-full flex-1 ",
          completed && "bg-blue-500"
        )}
      />
    </div>
  );
};
